import axios from 'axios';

/**
 * @typedef {Object} Options
 * @property {boolean} defaultValue Fallback value for feature flag, default false
 * @property {boolean} fetchOnServer Whether to attempt to fetch feature flag on server, default true
 * @property {Object} attributes - Attributes to be passed to Growthbook for evaluation with feature, default empty object
 */

/**
 * @type {Options}
 */
const DEFAULT_OPTIONS = {
  defaultValue: false,
  fetchOnServer: true,
  attributes: {}
};

/**
 * Allows evaluation of feature flags via Growthbook within both client and server contexts.
 *
 * This allows working within component fetch() methods as well as asyncData() methods, supporting both
 * server side routing and client side routing.
 *
 * Within a server context, this function will utilize the server/growthbook.js isFeatureOn method directly. Attributes
 * will be passed directly to this method.
 *
 * Within a client context, this function will utilize the /api/growthbook/:flag API to evaluate the flag. Attributes
 * will be passed to this API via query parameters. (Member related attributes should not need to be explicitly added since
 * they can be retrieved on the server side)
 *
 * @param {string} feature The name or key of the feature to evaluate
 * @param {Options} options Fallback value for feature flag, default false
 * @returns {Promise<boolean>} Whether the flag is on or off
 */
export async function isFeatureOn(feature, options = {}) {
  options = Object.assign({}, DEFAULT_OPTIONS, options);
  try {
    // Server feature flag fetching
    // process.server when accessed in modules will always be undefined
    if ((process.server || typeof process.server === 'undefined') && options.fetchOnServer) {
      const {
        isFeatureOn: _isFeatureOn
      } = await import('../server/growthbook');
      return _isFeatureOn(feature, options.attributes);
    }

    // Client feature flag fetching
    const attributeQueryString = new URLSearchParams(options.attributes).toString();
    return (await axios.get(`/api/growthbook/${feature}?${attributeQueryString}`)).data.isOn;
  } catch (err) {
    console.warn(`[growthbook] error getting flag ${feature}`);
    return options.defaultValue;
  }
}

/**
 * Allows evaluation of feature flag values via Growthbook within both client and server contexts.
 *
 * @param {string} feature The key of the feature to evaluate
 * @param {Options} options Options for feature flag
 * @returns {Promise<string || JSON || number>} Value of feature
 */
export async function getFeatureValue(feature, options = {}) {
  options = Object.assign({}, DEFAULT_OPTIONS, options);
  try {
    // Server feature value fetching
    // process.server when accessed in modules will always be undefined
    if ((process.server || typeof process.server === 'undefined') && options.fetchOnServer) {
      const {
        getFeatureValue: _getFeatureValue
      } = await import('../server/growthbook');
      return (await _getFeatureValue(feature, options.attributes)) || options.defaultValue;
    }

    // Client feature value fetching
    return (await axios.get(`/api/growthbook/${feature}?featureValue=true`)).data.featureValue;
  } catch (err) {
    console.warn(`[growthbook] error getting feature flag value ${feature}`);
    return options.defaultValue;
  }
}